import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listarAdminAplicaciones,
  obtenerFiltrosAdminAplicaciones,
} from '../../services/adminAplicaciones';
import AppShell from '../../components/layout/AppShell';
import StatusBadge from '../../components/ui/StatusBadge';
import EmptyState from '../../components/ui/EmptyState';
import Icon from '../../components/ui/Icon';
import Button from '../../components/ui/Button';
import AdminFiltrosAplicaciones from '../../components/admin/AdminFiltrosAplicaciones';

const FILTROS_DEFAULT = {
  estado: 'ACTIVA',
  idproductor: null,
  authUserIdEncargado: null,
  fechaDesde: null,
  fechaHasta: null,
  busqueda: null,
};

const PAGE_SIZE = 25;

function formatFecha(fechaaplicacion) {
  if (!fechaaplicacion) return '—';
  try {
    return new Date(`${fechaaplicacion}T00:00:00`).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return fechaaplicacion;
  }
}

function formatHa(ha) {
  if (ha === null || ha === undefined) return '—';
  return `${Number(ha).toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ha`;
}

function contarFiltrosActivos(filtros) {
  let count = 0;
  if (filtros.estado !== FILTROS_DEFAULT.estado) count += 1;
  if (filtros.idproductor) count += 1;
  if (filtros.authUserIdEncargado) count += 1;
  if (filtros.fechaDesde) count += 1;
  if (filtros.fechaHasta) count += 1;
  if (filtros.busqueda) count += 1;
  return count;
}

function OrigenBadge({ origen }) {
  const esWebapp = origen === 'webapp';
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
        esWebapp ? 'bg-gray-50 text-gray-500' : 'bg-gray-50 text-gray-400'
      }`}
    >
      {esWebapp ? 'App Campo' : 'WhatsApp'}
    </span>
  );
}

function AdminAplicacionesPage() {
  const navigate = useNavigate();

  const [filtros, setFiltros] = useState(FILTROS_DEFAULT);
  const [productores, setProductores] = useState([]);
  const [encargados, setEncargados] = useState([]);

  const [aplicaciones, setAplicaciones] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFiltros, setShowFiltros] = useState(false);

  useEffect(() => {
    obtenerFiltrosAdminAplicaciones().then((result) => {
      if (result.ok) {
        setProductores(result.productores);
        setEncargados(result.encargados);
      }
    });
  }, []);

  const cargar = useCallback(
    (pageToLoad, { append = false } = {}) => {
      setLoading(true);
      setError('');
      listarAdminAplicaciones({ ...filtros, page: pageToLoad, pageSize: PAGE_SIZE }).then((result) => {
        if (!result.ok) {
          setError(result.message);
          setLoading(false);
          return;
        }
        setAplicaciones((prev) => (append ? [...prev, ...result.aplicaciones] : result.aplicaciones));
        setTotal(result.total);
        setPage(result.page);
        setLoading(false);
      });
    },
    [filtros]
  );

  // Cualquier cambio de filtro vuelve a página 1 y reemplaza el listado
  // (con un pequeño debounce para no disparar una consulta por cada tecla
  // en el buscador).
  useEffect(() => {
    const timer = setTimeout(() => cargar(1, { append: false }), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros]);

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);
  const filtrosActivos = useMemo(() => contarFiltrosActivos(filtros), [filtros]);
  const hayMas = aplicaciones.length < total;

  const handleLimpiarFiltros = () => {
    setFiltros(FILTROS_DEFAULT);
    setShowFiltros(false);
  };

  const filaAAplicacion = (a) => (
    <>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">{formatFecha(a.fechaaplicacion)}</p>
        <div className="flex items-center gap-2">
          <OrigenBadge origen={a.origen} />
          <StatusBadge estado={a.estado} />
        </div>
      </div>
      <p className="mt-2 font-semibold text-gray-900">{a.productor_nombre || '—'}</p>
      <p className="text-sm text-gray-500">{a.encargado_nombre || '—'}</p>
      <p className="mt-2 text-sm text-gray-600">
        {a.huerto || '—'} · {a.cuartel || '—'}
      </p>
      <p className="mt-1 text-xs text-gray-400">
        {formatHa(a.ha)} · {a.cantidad_productos} producto{a.cantidad_productos === 1 ? '' : 's'}
      </p>
    </>
  );

  return (
    <AppShell title="Aplicaciones">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-xl font-bold text-gray-900">Aplicaciones</h1>
          <p className="text-sm text-gray-500">{total} en total</p>
        </div>

        {/* Filtros: barra siempre visible en desktop, botón + bottom sheet en mobile. */}
        <div className="hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm lg:block">
          <AdminFiltrosAplicaciones
            idPrefix="desktop"
            filtros={filtros}
            onChange={setFiltros}
            productores={productores}
            encargados={encargados}
            onLimpiar={handleLimpiarFiltros}
          />
        </div>

        <div className="lg:hidden">
          <button
            type="button"
            onClick={() => setShowFiltros(true)}
            className="flex min-h-[48px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
          >
            <Icon name="clipboard" className="h-5 w-5" />
            Filtros{filtrosActivos > 0 ? ` (${filtrosActivos})` : ''}
          </button>
        </div>

        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-center text-sm text-red-700">{error}</p>}

        {loading && aplicaciones.length === 0 ? (
          <p className="text-center text-sm text-gray-500">Cargando...</p>
        ) : aplicaciones.length === 0 ? (
          <EmptyState message="No hay aplicaciones para estos filtros." />
        ) : (
          <>
            <div className="hidden overflow-hidden rounded-2xl border border-gray-100 bg-white lg:block">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Fecha</th>
                    <th className="px-4 py-3 font-medium">Productor</th>
                    <th className="px-4 py-3 font-medium">Usuario</th>
                    <th className="px-4 py-3 font-medium">Ubicación</th>
                    <th className="px-4 py-3 font-medium">Sup.</th>
                    <th className="px-4 py-3 font-medium">Productos</th>
                    <th className="px-4 py-3 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {aplicaciones.map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => navigate(`/admin/aplicaciones/${a.id}`)}
                      className="cursor-pointer transition-colors hover:bg-gray-50"
                    >
                      <td className="px-4 py-3 text-gray-900">
                        <div className="flex items-center gap-2">
                          {formatFecha(a.fechaaplicacion)}
                          <OrigenBadge origen={a.origen} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{a.productor_nombre || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{a.encargado_nombre || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {a.huerto || '—'} · {a.cuartel || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{formatHa(a.ha)}</td>
                      <td className="px-4 py-3 text-gray-600">{a.cantidad_productos}</td>
                      <td className="px-4 py-3">
                        <StatusBadge estado={a.estado} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
                <Button
                  variant="secondary"
                  className="w-auto px-4"
                  disabled={page <= 1 || loading}
                  onClick={() => cargar(page - 1, { append: false })}
                >
                  Anterior
                </Button>
                <p className="text-sm text-gray-500">
                  Página {page} de {totalPages}
                </p>
                <Button
                  variant="secondary"
                  className="w-auto px-4"
                  disabled={page >= totalPages || loading}
                  onClick={() => cargar(page + 1, { append: false })}
                >
                  Siguiente
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-3 lg:hidden">
              {aplicaciones.map((a) => (
                <div
                  key={a.id}
                  onClick={() => navigate(`/admin/aplicaciones/${a.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') navigate(`/admin/aplicaciones/${a.id}`);
                  }}
                  className="cursor-pointer rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-colors hover:border-green-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                >
                  {filaAAplicacion(a)}
                </div>
              ))}

              {hayMas && (
                <Button
                  variant="secondary"
                  disabled={loading}
                  onClick={() => cargar(page + 1, { append: true })}
                >
                  {loading ? 'Cargando...' : 'Mostrar más'}
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Bottom sheet de filtros (mobile/tablet) */}
      {showFiltros && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 lg:hidden"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowFiltros(false)}
        >
          <div
            className="max-h-[85vh] w-full overflow-y-auto rounded-t-2xl bg-white p-5 pb-[calc(env(safe-area-inset-bottom)+20px)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Filtros</h3>
              <button
                type="button"
                onClick={() => setShowFiltros(false)}
                aria-label="Cerrar filtros"
                className="flex h-11 w-11 items-center justify-center rounded-full text-gray-500 hover:bg-gray-50"
              >
                <Icon name="close" />
              </button>
            </div>

            <AdminFiltrosAplicaciones
              idPrefix="mobile"
              filtros={filtros}
              onChange={setFiltros}
              productores={productores}
              encargados={encargados}
              onLimpiar={handleLimpiarFiltros}
            />

            <Button className="mt-6" onClick={() => setShowFiltros(false)}>
              Ver resultados
            </Button>
          </div>
        </div>
      )}
    </AppShell>
  );
}

export default AdminAplicacionesPage;
